// import external libraries
import { useTranslation } from 'react-i18next';

// import components
import { Tooltip } from '@mui/material';
import { Icon } from '@sdb/planning-ui';

// import utils
import { getIconColor, getIconType } from 'components/mrp/mrp.utils';

// import styles
import { EntityTypeButton, EntityTypeRow } from './EntityTypeToggle.styles';

// import types
import type { PlanningEntityType } from 'generated/index';
import type { EntityTypeToggleProps } from './EntityTypeToggle.types';

/**
 * A single choice between planning entity types, drawn as the same symbols the grids put in front of an
 * entity's name. Exactly one is active: the caller asks for one type at a time.
 *
 * The active symbol carries the entity's own colour and the rest grey out. Every symbol on screen is
 * pickable: a type the caller cannot handle is left out of `types` rather than shown disabled.
 *
 * Used by the takenlijst for its row type, and by the appointments overview for who a plan unit covers.
 */
const EntityTypeToggle = <Type extends PlanningEntityType>({
  types,
  activeType,
  onChange,
  isVertical
}: EntityTypeToggleProps<Type>) => {
  'use memo';

  const { t } = useTranslation('common');

  return (
    <EntityTypeRow isVertical={isVertical}>
      {types.map((type) => {
        const label = t(`mrp.entityTypeMap.${type}.single`);
        const isActive = type === activeType;

        return (
          <Tooltip key={type} title={label}>
            <EntityTypeButton
              size="small"
              aria-label={label}
              aria-pressed={isActive}
              isActive={isActive}
              activeColor={getIconColor(type)}
              nudgeRight={type === 'CLIENT'}
              // Exactly one type is always active, so pressing the active one is a no-op rather than a
              // clear — an empty list would be a confusing thing to get from pressing a symbol.
              onClick={() => !isActive && onChange(type)}
            >
              <Icon type={getIconType(type)} />
            </EntityTypeButton>
          </Tooltip>
        );
      })}
    </EntityTypeRow>
  );
};

export default EntityTypeToggle;
